import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed } from "lucide-react";
import { MapSearch } from "./MapSearch";
import { MapPlaceholder } from "./MapPlaceholder";
import { ESRI_TILES, ESRI_ATTRIBUTION } from "../lib/maps";
import { attachRasterHealth } from "../lib/mapHealth";

const pinIcon = L.divIcon({ className: "", html: '<div class="pin-marker" data-testid="map-pin"></div>', iconSize: [28, 28], iconAnchor: [14, 28] });
const useLocate = (onFound) => {
  const [geoMsg, setGeoMsg] = useState("");
  const locate = () => {
    if (!navigator.geolocation) return setGeoMsg("Location off — tap the map to drop a pin");
    setGeoMsg("Locating…");
    navigator.geolocation.getCurrentPosition(
      (pos) => { onFound({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }); setGeoMsg(""); },
      () => setGeoMsg("Location off — search an address instead"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  return [geoMsg, locate];
};

/* Raster picker (Esri static basemap tiles) — only when REACT_APP_ESRI_API_KEY is set. */
export const RasterPicker = ({ pin, onPin, onFail, onReady }) => {
  const el = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [geoMsg, locate] = useLocate((p) => { map.current.setView([p.lat, p.lng], 17); onPin(p); });

  useEffect(() => {
    const m = L.map(el.current, { zoomControl: false, attributionControl: true }).setView(pin ? [pin.lat, pin.lng] : [-36.8485, 174.7633], pin ? 15 : 12);
    const layer = L.tileLayer(ESRI_TILES, { maxZoom: 19, crossOrigin: "anonymous", attribution: ESRI_ATTRIBUTION }).addTo(m);
    const dispose = attachRasterHealth(layer, { onFail, onReady });
    m.on("click", (e) => onPin({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) }));
    map.current = m;
    el.current.querySelectorAll(".leaflet-control-attribution").forEach((n) => { n.classList.add("notranslate"); n.setAttribute("translate", "no"); });
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(el.current);
    return () => { dispose(); ro.disconnect(); m.remove(); map.current = null; marker.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current) return;
    if (!pin) { marker.current?.remove(); marker.current = null; return; }
    if (!marker.current) {
      marker.current = L.marker([pin.lat, pin.lng], { icon: pinIcon, draggable: true }).addTo(map.current);
      marker.current.on("dragend", () => { const p = marker.current.getLatLng(); onPin({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) }); });
    } else marker.current.setLatLng([pin.lat, pin.lng]);
  }, [pin, onPin]);

  return (
    <>
      <div ref={el} className="map-canvas leaflet-fill" data-testid="map-raster" />
      <MapSearch getCenter={() => { const c = map.current?.getCenter(); return c ? { lat: c.lat, lng: c.lng } : null; }} onLocate={locate}
        onSelect={({ lat, lng, label }) => { map.current.setView([lat, lng], 17); onPin({ lat, lng }, label); }} />
      <button type="button" data-testid="geolocate-button" className="map-fab" onClick={locate} aria-label="Use my location"><LocateFixed size={20} /></button>
      {geoMsg && <div className="map-toast" data-testid="geo-fallback">{geoMsg}</div>}
      {!pin && <div className="map-hint map-hint-low" data-testid="map-hint">Tap the map to drop a pin</div>}
    </>
  );
};

/* No map tiles available (no WebGL + no raster key, or both providers failed): search + "use my location" still set the pin. */
export const SearchOnlyPicker = ({ pin, onPin }) => {
  const [geoMsg, locate] = useLocate(onPin);
  return (
    <MapPlaceholder lat={pin?.lat} lng={pin?.lng} className="map-canvas">
      <MapSearch getCenter={() => pin || null} onLocate={locate} onSelect={({ lat, lng, label }) => onPin({ lat, lng }, label)} />
      <button type="button" data-testid="geolocate-button" className="map-fab" onClick={locate} aria-label="Use my location"><LocateFixed size={20} /></button>
      {geoMsg && <div className="map-toast" data-testid="geo-fallback">{geoMsg}</div>}
      {!pin && <div className="map-hint map-hint-low" data-testid="map-hint">Search an address or use your location to set the pin</div>}
    </MapPlaceholder>
  );
};

export const LeafletPicker = (props) => (ESRI_TILES ? <RasterPicker {...props} /> : <SearchOnlyPicker {...props} />);
